export type HealthTopic =
  | 'cancer'
  | 'cardiovascular'
  | 'careTransitions'
  | 'diabetes'
  | 'generic'
  | 'kidney'
  | 'maternal'
  | 'mentalAndBehavioral'
  | 'painManagement'
  | 'pediatric'
  | 'respiratory'
  | 'wellbeingAndSocialSupport'
  | 'womens';

export type HealthTopicAPIString =
  | 'CANCER_SUPPORT'
  | 'CARDIOVASCULAR_HEALTH'
  | 'CARE_TRANSITIONS_AND_HOSPITAL_STAY'
  | 'DIABETES_AND_PRE_DIABETES_SUPPORT'
  | 'KIDNEY_HEALTH'
  | 'MATERNAL_HEALTH'
  | 'MENTAL_AND_BEHAVIORAL_HEALTH'
  | 'PAIN_MANAGEMENT'
  | 'PEDIATRIC_HEALTH'
  | 'RESPIRATORY_HEALTH'
  | 'WELLNESS_AND_SOCIAL_SUPPORT'
  | 'WOMENS_HEALTH';

export type CareProgramTopicCardVariant = 'square' | 'horizontal';

export type EnrollmentStatus = 'not_enrolled' | 'requested' | 'enrolled' | 'declined' | 'completed';

export enum EnrollmentDialogState {
  NONE = 'none',
  CONFIRM_ENROLL = 'confirmEnroll',
  REQUEST_SENT = 'requestSent',
  REQUEST_FAILED = 'requestFailed',
  GO_TO_CHAT = 'goToChat',
}

export enum CareProgramRequestStatus {
  REQUESTED = 'REQUESTED',
  APPROVED = 'APPROVED',
  DECLINED = 'DECLINED',
}

/**
 * API response for a single health topic
 */
export interface HealthTopicAPIResponse {
  identifier: HealthTopicAPIString;
}

export interface TransformedHealthTopic {
  identifier: HealthTopicAPIString;
  healthTopic: HealthTopic;
  displayName: string;
  iconName: string;
}
export interface TransformedLibraryArticle {
  id: number;
  title: string;
  description: string;
  contentUrl: string;
  contentType: string;
  imageUrl: string;
  isIndependent?: boolean;
  createdAt?: string;
}

/** Each possible "What to Expect" bullet item. */
export type WhatToExpectItem =
  | { type: 'checkInViewProgress' }
  | { type: 'checkIn' }
  | { type: 'educationalArticles' }
  | { type: 'daysOfContent'; daysCount: number };

export interface CareProgramDetail {
  /** Determines the icon and background color. */
  healthTopic: HealthTopic;
  /** Program title. */
  title: string;
  /** Program description. */
  summary: string;
  /** Up to four "What to Expect" items. */
  whatToExpect: WhatToExpectItem[];
  /** Program ID for enrollment requests. */
  programId: number;
  /** Current enrollment status for this program. */
  enrollmentStatus?: EnrollmentStatus;
}

/**
 * A single member benefit rendered in the benefit detail modal.
 */
export interface BenefitDetail {
  /** Benefit title. */
  title: string;
  /** Benefit description; supports multiple paragraphs via line breaks. */
  description: string;
  /** Banner illustration URL shown centered at the top of the modal. */
  imageUrl?: string;
  /** Optional contact phone number rendered as a `tel:` link. */
  phoneNumber?: string;
  /** Optional external CTA URL; when omitted no "Visit" button is shown. */
  visitUrl?: string;
}

export interface CareProgramMappingsAPIResponse {
  member_facing_name: string;
  member_facing_description: string;
  health_topics: HealthTopicAPIString[];
  health_goals: string[];
  is_member_visible: boolean;
  target_population: string;
  care_area: string;
  phase: string;
  biometric_type?: string | null;
}

export interface CareProgramSubscriptionAPIResponse {
  id: number;
  is_ended: boolean;
  start_date: string;
}

export type CareProgramType = 'Health Tracker' | 'Educational Program' | (string & {});

/**
 * Full API response for a single care program.
 */
export interface CareProgramAPIResponse {
  id: number;
  name: string;
  display_title: string;
  description: string;
  target_audience: string;
  program_length_days: number;
  rolling: boolean;
  tags: string[];
  care_program_type: CareProgramType;
  to_do_types: string[];
  average_task_rate: number;
  table_of_contents_url: string;
  care_program_mappings: CareProgramMappingsAPIResponse;
  care_program_subscriptions: CareProgramSubscriptionAPIResponse[];
  care_program_request_id: string;
  available_to_all: boolean;
}

export type CareProgramsAPIResponse = CareProgramAPIResponse[];

/**
 * Transformed care program mappings ready for UI display.
 */
export interface CareProgramMappings {
  memberFacingName: string;
  memberFacingDescription: string;
  healthTopics: HealthTopicAPIString[];
  healthGoals: string[];
  isMemberVisible: boolean;
  targetPopulation: string;
  careArea: string;
  phase: string;
}

/**
 * Transformed care program subscription ready for UI display.
 */
export interface CareProgramSubscription {
  id: number;
  isEnded: boolean;
  startDate: string;
}

/**
 * Transformed care program ready for UI display.
 */
export interface CareProgram {
  id: number;
  name: string;
  displayTitle: string;
  description: string;
  targetAudience: string;
  programLengthDays: number;
  rolling: boolean;
  tags: string[];
  programType: CareProgramType;
  todoTypes: string[];
  averageTaskRate: number;
  tableOfContentsUrl: string;
  mappings: CareProgramMappings;
  subscriptions: CareProgramSubscription[];
  requestId: string;
  availableToAll: boolean;
}
export interface Program {
  id: number;
  title: string;
  healthTopic: HealthTopic;
  programType: CareProgramType;
  enrollmentStatus: EnrollmentStatus;
}

export interface ProgramSection {
  programType: CareProgramType;
  title: string;
  description: string;
  programs: Program[];
  count: number;
}

export interface CareProgramTopicCardProps {
  /**
   * The health topic type - determines background color and icon
   */
  healthTopic: HealthTopic;
  /**
   * The title of the care program
   */
  title: string;
  /**
   * Click handler for the card
   */
  onClick?: () => void;
  /**
   * Whether the card is disabled
   */
  disabled?: boolean;
  /**
   * Whether the card is currently selected. Adds a brand primary outline.
   */
  selected?: boolean;
  /**
   * Custom aria-label for accessibility
   */
  ariaLabel?: string;
  /**
   * Visual variant of the card
   * @default 'square'
   */
  variant?: CareProgramTopicCardVariant;
}

export interface ContentSearchItemAPIResponse {
  id: number;
  title: string;
  url: string;
  content_type: string;
  updated_at?: string;
  locale?: string;
  description?: string;
  thumbnail_url?: string;
  favorite_id?: number | null;
}

export interface ContentSearchAPIResponse {
  care_programs: ContentSearchItemAPIResponse[];
  on_demand: ContentSearchItemAPIResponse[];
}

export interface ContentSearchParams {
  search: string;
  page?: number;
  pageSize?: number;
}

/** Accumulated searchContent cache value. care_programs and on_demand paginate independently. */
export interface ContentSearchQueryData {
  care_programs: ContentSearchItemAPIResponse[];
  on_demand: ContentSearchItemAPIResponse[];
  careProgramsHasMore: boolean;
  onDemandHasMore: boolean;
}

/** Enrollment initiator. */
export type CareProgramEnrollmentType = 'self' | 'care_team_recommended';

/** Preferred contact method. */
export type CareProgramCommunicationMethod = 'sms' | 'email' | 'app';

export type CareProgramEnrollmentStatus = 'pending' | 'approved' | 'active';

export interface CareProgramEnrollmentPreferences {
  communicationMethod: CareProgramCommunicationMethod;
  startDate: string;
  additionalNotes?: string;
}

export interface CareProgramEnrollmentMetadata {
  sourceLocation: string;
  timestamp: string;
}

/** Enroll POST request body. */
export interface CareProgramEnrollmentRequest {
  memberId: string;
  programId: number;
  enrollmentType: CareProgramEnrollmentType;
  preferences: CareProgramEnrollmentPreferences;
  metadata: CareProgramEnrollmentMetadata;
}

export interface CareProgramEnrollmentNextStep {
  action: string;
  description: string;
  dueDate: string;
}

export interface CareProgramEnrollmentProgramDetails {
  id: string;
  name: string;
  description: string;
}
export interface CareProgramEnrollmentResponse {
  enrollmentId: string;
  status: CareProgramEnrollmentStatus;
  enrolledAt: string;
  programDetails: CareProgramEnrollmentProgramDetails;
  nextSteps: CareProgramEnrollmentNextStep[];
}

export interface LibraryArticleFavorite {
  id: number;
  title: string;
  url: string;
  excerpt: string;
  program_user_id?: number;
}

export interface LibraryArticleFavoriteAddRequest {
  title: string;
  url: string;
  excerpt: string;
}

export type EngagementType = 'MEMBER_BENEFITS_VIEW';

export interface EngagementLogPayload {
  url: string | null;
  engagement_type: EngagementType;
}

export interface LogEngagementParams {
  memberUuid: string;
  body: EngagementLogPayload;
}
export interface Benefit {
  id: string;
  title: string;
  thumbnailImageUrl?: string;
  largerImageUrl?: string;
  description: string;
  link: string | null;
  vendorPhone: string | null;
  vendorName: string | null;
}

export interface BenefitAPIResponse {
  uuid: string;
  name: string;
  thumbnailImageUrl?: string;
  largerImageUrl?: string;
  description: string;
  link: string | null;
  vendorPhone: string | null;
  vendorName: string | null;
  category?: { uuid: string; name: string };
}

export interface BenefitsAPIResponse {
  page: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  benefits: BenefitAPIResponse[];
}

export interface BenefitsParams {
  memberUuid: string;
  page?: number;
  pageSize?: number;
  language: string;
}

export interface BenefitsQueryData {
  benefits: Benefit[];
  totalPages: number;
  lastLoadedPage: number;
}
